import { Alert, Box, Button, Card, CardActions, CardContent, Chip, Divider, Grid, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Paper, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Plan } from "../Plan/ChoosePlan";
import { AutoGraph, Bolt, Chat, Check, CheckCircle, Close, CloseSharp, CreditCard, Email, Groups, Info, Security, Star, Storage, Workspaces } from "@mui/icons-material";
import stripeConfig from "../../services/config_stripe.json";
import { KeyDriveBodyMax } from "../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMax";
import { KeyDriveBodyMin } from "../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { AddressElement, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useNavigate, useParams } from "react-router-dom";
import { CreateSubscription, downgradePlanAsync, upgradePlanAsync } from "../../services/PaymentService";
import { KeyDriveDialog } from "../../components/KeydriveComponents/Dialog/KeyDriveDialog";
import { useProfile } from "../../context/ProfileProvider/useProfile";
import { KeydrivePlan } from "../../model/UserModel";
import {  IDowngradePlan, IUpgradePlan } from "../../model/OrganizationModel";
import moment from "moment";
import { useAuth } from "../../context/AuthProvider/useAuth";
import { useTranslation } from 'react-i18next';
import { waUrlBuilderComercial } from "../MyDataPage/components/utils/waSupportBuilder";

type LoadingStep = 'validating' | 'creating-payment' | 'processing' | 'finalizing' | 'complete' | null;

export default function Plans() {

    const stripe = useStripe();
    const [pricingModal, setPricingModal] = useState<boolean>(false);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [successModal, setSuccessModal] = useState<boolean>(false);
    const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
    const [isDowngrading, setIsDowngrading] = useState<boolean>(false);
    const [isContractingSamePlan, setIsContractingSamePlan] = useState<boolean>(false);
    const [loadingStep, setLoadingStep] = useState<LoadingStep>(null);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const elements = useElements();
    const { nameOrg } = useParams();
    const navigate = useNavigate();
    const profile = useProfile();
    const [errorModal, setErrorModal] = useState<boolean>(false);
    const [upgradePlanSucess, setUpgradedPlanSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [isUserAlreadyDowngradedOrUpgraded, setIsUserAlreadyDowngradedOrUpgraded] = useState<boolean>(false);
    const [errorModalIsUpgradeOrDowngrade, setErrorModalIsUpgradeOrDowngrade] = useState<boolean>(false);
    const { t } = useTranslation();

    const navegate = useNavigate(); 
    const auth = useAuth();

    function handleSelectPlan(plan: Plan) {
        
        let currentUserPlan = profile?.signtureInformation?.currentPlan;
        let userPlanContracted = plans.filter(plan => plan.keydrivePlan == currentUserPlan)![0];

        if (profile?.signtureInformation?.contractDowngradeUpgrade != undefined && 
            profile?.signtureInformation?.contractDowngradeUpgrade?.contractAlreadyDowngradedOrUpgraded 
        ) {
            setIsUserAlreadyDowngradedOrUpgraded(true);
            return;
        }

        if (userPlanContracted == undefined) {
            setSelectedPlan(plan);
            setPricingModal(true);
        } else {
            if (userPlanContracted.codeValue < plan.codeValue) {
                setIsUpgrading(true);
                setIsDowngrading(false);
                setSelectedPlan(plan);
            } else if (userPlanContracted.codeValue > plan.codeValue) {
                setIsDowngrading(true);
                setIsUpgrading(false);
                setSelectedPlan(plan);
            } else if (userPlanContracted.codeValue == plan.codeValue) {
                setIsContractingSamePlan(true);
                setIsDowngrading(false);
                setIsUpgrading(false);
            } else {
                setSelectedPlan(plan);
                setPricingModal(true);
            }
        }

    }
    
    const getLoadingSteps = () => {
        return [
            t('ChoosePlan.validatingData'),
            t('ChoosePlan.creatingPaymentMethod'),
            t('ChoosePlan.processingSubscriptionStep'),
            t('ChoosePlan.finalizing'),
        ];
    };

    const getCurrentStepIndex = () => {
        switch (loadingStep) {
            case 'validating': return 0;
            case 'creating-payment': return 1;
            case 'processing': return 2;
            case 'finalizing': return 3;
            case 'complete': return 4;
            default: return 0;
        }
    };

    async function UpgradePlan(): Promise<void> {
        try {
            let requestObject: IUpgradePlan = {
                email: profile?.login!,
                internalName: nameOrg!,
                newPriceId: selectedPlan?.priceId!
            }

            await upgradePlanAsync(requestObject);

            setUpgradedPlanSuccess(true);
            setPricingModal(false);
        } catch (error: any) {
            setErrorModal(true);
            setErrorMessage(error.response.data);
        }
    }

    async function downgradePlan(): Promise<void> {
        try {
            let requestObject: IDowngradePlan = {
                email: profile?.login!,
                internalName: nameOrg!,
                newPriceId: selectedPlan?.priceId!,
                newPlan: selectedPlan?.keydrivePlan!
            }

            await downgradePlanAsync(requestObject);

            setUpgradedPlanSuccess(true);
            setPricingModal(false);

        } catch (error: any) {
            setErrorModal(true);
            setErrorMessage(error.response.data);
        }
    }
    
    async function handleSubscription() {
        setLoadingModal(true);
        
        try {
            setLoadingStep('validating');
            await new Promise(resolve => setTimeout(resolve, 800));

            const cardElement = elements?.getElement(CardElement);
            const addressElement = elements?.getElement(AddressElement);
            const addressElValue = await addressElement?.getValue();

            if (!addressElValue?.complete) {
                setLoadingModal(false);
                setLoadingStep(null);
                setErrorModal(true);
                return;
            }

            setLoadingStep('creating-payment');
            await new Promise(resolve => setTimeout(resolve, 1000));

            const stripePmRequest = await stripe?.createPaymentMethod({
                card: cardElement!,
                type: "card",
                billing_details: {
                    email: profile.login,
                    address: {
                        city: addressElValue?.value.address.city,
                        country: addressElValue?.value.address.country,
                        line1: addressElValue?.value.address.line1,
                        line2: addressElValue?.value.address.line2,
                        postal_code: addressElValue?.value.address.postal_code,
                        state: addressElValue?.value.address.state
                    },
                    name: addressElValue?.value.name,
                    phone: addressElValue?.value.phone!
                }
            });

            if (stripePmRequest?.error != undefined) {
                setLoadingModal(false);
                setLoadingStep(null);
                setErrorModal(true);
                return;
            }

            setLoadingStep('processing');
            await new Promise(resolve => setTimeout(resolve, 1000));

            await CreateSubscription({
                paymentMethodId: stripePmRequest?.paymentMethod?.id!,
                email: profile.login!,
                priceId: selectedPlan?.priceId!,
                Address: {
                    city: addressElValue?.value.address.city!,
                    country: addressElValue?.value.address.country!,
                    line1: addressElValue?.value.address.line1!,
                    line2: addressElValue?.value.address.line2!,
                    postalCode: addressElValue?.value.address.postal_code!,
                    state: addressElValue?.value.address.state!,
                    phone: addressElValue?.value.phone!
                },
                clientName: addressElValue?.value.name!,
                createdByOnboard: false,
                internalName: nameOrg!
            });

            setLoadingStep('finalizing');
            await new Promise(resolve => setTimeout(resolve, 800));

            setLoadingStep('complete');
            await new Promise(resolve => setTimeout(resolve, 1000));

            setLoadingModal(false);
            setLoadingStep(null);
            setSuccessModal(true);
            setPricingModal(false);
            
        } catch (error: any) {
            setLoadingModal(false);
            setLoadingStep(null);
            setErrorModal(true);
            setErrorMessage(error.response.data);
        }
    }

    const plans: Plan[] = [
            {
                name: t('ChoosePlan.essentialPlan'),
                price: 'R$197',
                period: t('ChoosePlan.perMonth'),
                priceId: stripeConfig.price_essential,
                codeValue: 1,
                keydrivePlan: KeydrivePlan.ESSENCIAL,
                features: [
                    { icon: <Groups fontSize="small" />, text: t('ChoosePlan.upToUsers', { count: 5 }) },
                    { icon: <Storage fontSize="small" />, text: t('ChoosePlan.storage', { size: '100 GB' }) },
                    { icon: <Workspaces fontSize="small" />, text: t('ChoosePlan.workspace', { count: 1 }) },
                    { icon: <Security fontSize="small" />, text: t('ChoosePlan.groupPermissions') },
                    { icon: <AutoGraph fontSize="small" />, text: t('ChoosePlan.versionControl') },
                    { icon: <Email fontSize="small" />, text: t('ChoosePlan.emailSupport') },
                ]
            },
            {
                name: t('ChoosePlan.professionalPlan'),
                price: 'R$497',
                period: t('ChoosePlan.perMonth'),
                priceId: stripeConfig.price_professional,            
                highlighted: true,
                badge: t('ChoosePlan.mostPopular'),
                codeValue: 2,
                keydrivePlan: KeydrivePlan.PROFISSIONAL,
                features: [
                    { icon: <Groups fontSize="small" />, text: t('ChoosePlan.upToUsers', { count: 15 }) },
                    { icon: <Storage fontSize="small" />, text: t('ChoosePlan.storage', { size: '1 TB' }) },
                    { icon: <Workspaces fontSize="small" />, text: t('ChoosePlan.workspaces', { count: 5 }) },
                    { icon: <Bolt fontSize="small" />, text: t('ChoosePlan.workflowRules') },
                    { icon: <Security fontSize="small" />, text: t('ChoosePlan.externalAPIs') },
                    { icon: <Chat fontSize="small" />, text: t('ChoosePlan.chatSupport') },
                ]
            },
            {
                name: t('ChoosePlan.scalePlan'),
                price: 'R$2.297',
                period: t('ChoosePlan.perMonth'),
                priceId: stripeConfig.price_scale,
                codeValue: 3,
                keydrivePlan: KeydrivePlan.SCALE,
                features: [
                    { icon: <Groups fontSize="small" />, text: t('ChoosePlan.upToUsers', { count: 50 }) },
                    { icon: <Storage fontSize="small" />, text: t('ChoosePlan.storage', { size: '5 TB' }) },
                    { icon: <Workspaces fontSize="small" />, text: t('ChoosePlan.unlimitedWorkspaces') },
                    { icon: <Security fontSize="small" />, text: t('ChoosePlan.cadViewer') },
                    { icon: <Storage fontSize="small" />, text: t('ChoosePlan.coldStorage') },
                    { icon: <AutoGraph fontSize="small" />, text: t('ChoosePlan.advancedReports') },
                    { icon: <Star fontSize="small" />, text: t('ChoosePlan.dedicatedSupport') },
                ]
            }
    ];

    return (
        <Box>
            
        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 1200, mx: 'auto' }}>
                {plans.map((plan, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Card 
                            elevation={plan.highlighted ? 8 : 2}
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                border: plan.highlighted ? '2px solid #22638D' : '1px solid #e0e0e0',
                                transform: plan.highlighted ? 'scale(1.02)' : 'scale(1)',
                                transition: 'all 0.3s ease',
                                background: plan.highlighted 
                                    ? 'linear-gradient(135deg, #ffffff 0%, #f5f9fc 100%)'
                                    : '#ffffff',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: plan.highlighted ? 12 : 6,
                                }
                            }}
                        >
                            {plan.badge && (
                                <Chip 
                                    label={plan.badge}
                                    color="primary"
                                    size="small"
                                    sx={{ 
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        bgcolor: '#22638D',
                                        fontWeight: 600,
                                        height: 24,
                                        fontSize: '0.75rem'
                                    }}
                                />
                            )}
                            
                            <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
                                {/* Plan Name */}
                                <Typography
                                    sx={{
                                        fontSize: '1.25rem',
                                        fontWeight: 700,
                                        color: plan.highlighted ? '#22638D' : '#0a1929',
                                        mb: 1.5
                                    }}
                                >
                                    {plan.name}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                {/* Features */}
                                <List sx={{ p: 0 }}>
                                    {plan.features.map((feature, idx) => (
                                        <ListItem key={idx} sx={{ px: 0, py: 0.5, minHeight: 32 }}>
                                            <ListItemIcon sx={{ minWidth: 28, color: '#22638D' }}>
                                                {feature.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={feature.text}
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    color: '#37474f',
                                                    lineHeight: 1.4
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>

                            <CardActions sx={{ p: 2.5, pt: 1 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => {
                                        window.open(waUrlBuilderComercial(), '_blank')
                                    }}
                                    sx={{
                                        py: 1,
                                        bgcolor: plan.highlighted ? '#22638D' : '#546e7a',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            bgcolor: plan.highlighted ? '#1a4f6d' : '#37474f',
                                        }
                                    }}
                                >
                                    {t('ChoosePlan.subscribeNow')}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            { profile?.signtureInformation?.currentPlan == KeydrivePlan.PERSONAL && (
                <Alert sx={{ marginTop: 4, backgroundColor: "#22638D", }} severity="info" icon={<Info sx={{ color: "white" }} />}>
                    <Typography color="white">
                        {t('ChoosePlan.personalPlanAlert')}
                    </Typography>
                </Alert>
            ) }
        </Grid>

            {/* Payment Modal */}
            { pricingModal && (
                <KeyDriveDialog
                    hidden={
                        loadingModal || successModal || errorModal
                    }
                    keyDriveDialogButtons={{
                        leftButtonMethod: () => { setPricingModal(false) },
                        rightButtonMethod: () => {
                            handleSubscription();
                        },
                        rightButtonText: t('ChoosePlan.confirmSubscription'),
                        leftButtonText: t('ChoosePlan.cancel')
                    }}
                    keyDriveDialogContent={{
                        dialogContent: (
                            <Box sx={{ mt: 3 }}>
                                {/* Selected Plan Info */}
                                {selectedPlan && (
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 3, 
                                            mb: 4, 
                                            bgcolor: '#f5f9fc',
                                            border: '2px solid #22638D',
                                            borderRadius: 2
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box>
                                                <KeyDriveBodyMax 
                                                    color='#0a1929'
                                                    needBold={true}
                                                    needEllipses={false}
                                                    text={`${t('ChoosePlan.plan')} ${selectedPlan.name}`}
                                                />
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Box component="span" sx={{ fontSize: '1.8rem', fontWeight: 700, color: '#22638D' }}>
                                                    {selectedPlan.price}
                                                </Box>
                                                <Box component="span" sx={{ fontSize: '1rem', color: '#546e7a', ml: 0.5 }}>
                                                    {selectedPlan.period}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>
                                )}

                                {/* Card Element */}
                                <Box sx={{ mb: 3 }}>
                                    <KeyDriveBodyMin 
                                        color='#0a1929'
                                        needBold={true}
                                        needEllipses={false}
                                        text={t('ChoosePlan.cardDetails')}
                                    />
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 2.5, 
                                            mt: 1.5,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 2,
                                            '&:focus-within': {
                                                borderColor: '#22638D',
                                                boxShadow: '0 0 0 2px rgba(34, 99, 141, 0.1)'
                                            }
                                        }}
                                    >
                                        <CardElement 
                                            options={{
                                                style: {
                                                    base: {
                                                        fontSize: "16px",
                                                        color: "#0a1929",
                                                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                                        "::placeholder": {
                                                            color: "#9e9e9e",
                                                        },
                                                    },
                                                    invalid: {
                                                        color: "#d32f2f",
                                                    },
                                                },
                                                hidePostalCode: true,
                                            }}
                                        />
                                    </Paper>
                                </Box>

                                {/* Address Element */}
                                <Box>
                                    <KeyDriveBodyMin 
                                        color='#0a1929'
                                        needBold={true}
                                        needEllipses={false}
                                        text={t('ChoosePlan.billingAddress')}
                                    />
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 2.5, 
                                            mt: 1.5,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 2,
                                            '& .StripeElement': {
                                                padding: 0,
                                            }
                                        }}
                                    >
                                        <AddressElement
                                            options={{
                                                allowedCountries: ["BR", "US"],
                                                mode: "billing",
                                                fields: {
                                                    phone: "always",
                                                },
                                                display: {
                                                    name: "full",
                                                },
                                            }}
                                        />
                                    </Paper>
                                </Box>
                            </Box>
                        ),
                        dialogIcon: <CreditCard sx={{ color: "#22638D", width: "48px", height: "48px" }}/>,
                        dialogTitle: t('ChoosePlan.completeSubscription'),
                        handleTogglDialog: () => {},
                        openDialog: pricingModal
                    }}
                />
            ) }

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => {},
                    rightButtonMethod: () => {},
                    rightButtonText: undefined,
                    leftButtonText: undefined
                }}
                keyDriveDialogContent={{
                    dialogContent: (
                        <Box sx={{ 
                            textAlign: 'center',
                            py: 2
                        }}>
                            <KeyDriveBodyMax 
                                color='#0a1929'
                                needBold={true}
                                needEllipses={false}
                                text={t('ChoosePlan.processingSubscription')}
                            />
                            <Box sx={{ mt: 4, mb: 2, px: 2 }}>
                                <Stepper activeStep={getCurrentStepIndex()} alternativeLabel>
                                    {getLoadingSteps().map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                            <LinearProgress 
                                sx={{ 
                                    mt: 3,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: '#22638D'
                                    }
                                }} 
                            />
                        </Box>
                    ),
                    handleTogglDialog: () => {},
                    openDialog: loadingModal
                }}
            />

            {/* Success Modal */}
            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => {},
                    rightButtonMethod: () => {
                        setSuccessModal(false);
                        navigate("/");
                    },
                    rightButtonText: t('ChoosePlan.startUsing'),
                    leftButtonText: undefined
                }}
                keyDriveDialogContent={{
                    dialogContent: (
                        <Box sx={{ 
                            textAlign: 'center',
                            py: 2
                        }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: '#e8f5e9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px'
                            }}>
                                <CheckCircle sx={{ color: "#4caf50", width: "56px", height: "56px" }} />
                            </Box>
                            
                            <KeyDriveBodyMax 
                                color='#0a1929'
                                needBold={true}
                                needEllipses={false}
                                text={t('ChoosePlan.subscriptionCompleted')}
                            />
                            
                            <Box sx={{ mt: 2, mb: 3 }}>
                                <KeyDriveBodyMin 
                                    color='#546e7a'
                                    needBold={false}
                                    needEllipses={false}
                                    text={t('ChoosePlan.congratulations')}
                                />
                            </Box>

                            {selectedPlan && (
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        p: 3, 
                                        mt: 3,
                                        bgcolor: '#f5f9fc',
                                        border: '1px solid #22638D',
                                        borderRadius: 2,
                                        textAlign: 'left'
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>
                                        <KeyDriveBodyMin 
                                            color='#546e7a'
                                            needBold={true}
                                            needEllipses={false}
                                            text={t('ChoosePlan.subscriptionDetails')}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <KeyDriveBodyMin 
                                            color='#0a1929'
                                            needBold={false}
                                            needEllipses={false}
                                            text={`${t('ChoosePlan.plan')}:`}
                                        />
                                        <KeyDriveBodyMin 
                                            color='#0a1929'
                                            needBold={true}
                                            needEllipses={false}
                                            text={selectedPlan.name}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <KeyDriveBodyMin 
                                            color='#0a1929'
                                            needBold={false}
                                            needEllipses={false}
                                            text={t('ChoosePlan.value')}
                                        />
                                        <KeyDriveBodyMin 
                                            color='#22638D'
                                            needBold={true}
                                            needEllipses={false}
                                            text={`${selectedPlan.price}${selectedPlan.period}`}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <KeyDriveBodyMin 
                                            color='#0a1929'
                                            needBold={false}
                                            needEllipses={false}
                                            text={`${t('ChoosePlan.email')}:`}
                                        />
                                        <KeyDriveBodyMin 
                                            color='#0a1929'
                                            needBold={false}
                                            needEllipses={false}
                                            text={profile?.login!}
                                        />
                                    </Box>
                                </Paper>
                            )}

                            <Box sx={{ mt: 3 }}>
                                <KeyDriveBodyMin 
                                    color='#78909c'
                                    needBold={false}
                                    needEllipses={false}
                                    text={t('ChoosePlan.logoutToRefreshSubscription')}
                                />
                            </Box>
                        </Box>
                    ),
                    handleTogglDialog: () => {},
                    openDialog: successModal
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => {},
                    rightButtonMethod: () => {
                        setErrorModal(false);
                        if (!errorModalIsUpgradeOrDowngrade)
                        setPricingModal(true);
                    },
                    rightButtonText: errorModalIsUpgradeOrDowngrade ? "Voltar para planos" : t('ChoosePlan.tryAgain'),
                    leftButtonText: undefined
                }}
                keyDriveDialogContent={{
                    dialogContent: (
                        <Box sx={{ 
                            textAlign: 'center',
                            py: 2
                        }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: '#ffebee',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px'
                            }}>
                                <Box sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '50%',
                                    border: '4px solid #d32f2f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}>
                                    <Box sx={{
                                        width: 4,
                                        height: 24,
                                        bgcolor: '#d32f2f',
                                        borderRadius: 1,
                                        transform: 'rotate(45deg)',
                                        position: 'absolute'
                                    }} />
                                    <Box sx={{
                                        width: 4,
                                        height: 24,
                                        bgcolor: '#d32f2f',
                                        borderRadius: 1,
                                        transform: 'rotate(-45deg)',
                                        position: 'absolute'
                                    }} />
                                </Box>
                            </Box>
                            
                            <KeyDriveBodyMax 
                                color='#0a1929'
                                needBold={true}
                                needEllipses={false}
                                text={t('ChoosePlan.errorProcessing')}
                            />

                            { errorMessage != undefined && (
                                <KeyDriveBodyMin 
                                    color='#0a1929'
                                    needBold={true}
                                    needEllipses={false}
                                    text={errorMessage}
                                    sxStyles={{
                                        mt: 2,
                                    }}
                                />
                            ) }

                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 3, 
                                    mt: 3,
                                    bgcolor: '#fff3e0',
                                    border: '1px solid #ff9800',
                                    borderRadius: 2,
                                    textAlign: 'left'
                                }}
                            >
                                <KeyDriveBodyMin 
                                    color='#e65100'
                                    needBold={true}
                                    needEllipses={false}
                                    text={t('ChoosePlan.whatToDoNow')}
                                />
                                <Box sx={{ mt: 0.5 }}>
                                    <KeyDriveBodyMin 
                                        color='#5d4037'
                                        needBold={false}
                                        needEllipses={false}
                                        text={t('ChoosePlan.alreadyHaveSubscriptionError')}
                                    />
                                </Box>
                                <Box sx={{ mt: 1.5 }}>
                                    <KeyDriveBodyMin 
                                        color='#5d4037'
                                        needBold={false}
                                        needEllipses={false}
                                        text={t('ChoosePlan.checkCardData')}
                                    />
                                </Box>
                                <Box sx={{ mt: 0.5 }}>
                                    <KeyDriveBodyMin 
                                        color='#5d4037'
                                        needBold={false}
                                        needEllipses={false}
                                        text={t('ChoosePlan.ensureFieldsFilled')}
                                    />
                                </Box>
                                <Box sx={{ mt: 0.5 }}>
                                    <KeyDriveBodyMin 
                                        color='#5d4037'
                                        needBold={false}
                                        needEllipses={false}
                                        text={t('ChoosePlan.contactSupport')}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    ),
                    handleTogglDialog: () => {},
                    openDialog: errorModal
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => {},
                    rightButtonMethod: () => {
                        navegate("/");
                    },
                    rightButtonText: "Voltar",
                    leftButtonText: undefined
                }}
                keyDriveDialogContent={{
                    dialogContent: (
                        <Box sx={{ 
                            textAlign: 'center',
                            py: 2
                        }}>
                            <KeyDriveBodyMin 
                                color="#2D2D2D"
                                needBold
                                needEllipses={false}
                                text={"Você possui uma assinatura personal. Para um upgrade de planos, consulte nosso comercial."}
                            />
                        </Box>
                    ),
                    dialogIcon: <Close />,
                    handleTogglDialog: () => {},
                    openDialog: profile?.signtureInformation?.currentPlan == KeydrivePlan.PERSONAL
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => {},
                    rightButtonMethod: () => {
                        auth.logout();
                    },
                    rightButtonText: "Sair",
                    leftButtonText: undefined
                }}
                dialogTitleCenter
                keyDriveDialogContent={{
                    dialogTitle: "Você atualizou seu plano!",
                    dialogIcon: <Check />,
                    dialogContent: (
                        <KeyDriveBodyMin 
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                            text={"Pedimos que atualize a página saindo e entrando novamente no aplicativo para que suas informações sejam atualizadas."}
                        />
                    ),
                    handleTogglDialog: () => {},
                    openDialog: upgradePlanSucess
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonMethod: () => {
                        UpgradePlan();
                        setIsUpgrading(false);
                    },
                    rightButtonText: "Continuar",
                    leftButtonMethod: () => {
                        setIsUpgrading(false);
                    },
                    leftButtonText: "Cancelar"
                }}
                dialogTitleCenter
                keyDriveDialogContent={{
                    dialogTitle: "Você está prestes a fazer um upgrade no seu plano!",
                    dialogIcon: <Check />,
                    dialogContent: (
                        <KeyDriveBodyMin 
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                            text={"Ao clicar em continuar você concorda em trocar de plano assinado. Você terá que colocar suas informações novamente para validação e após isso, seu plano será atualizado"}
                        />
                    ),
                    handleTogglDialog: () => {},
                    openDialog: profile?.signtureInformation?.currentPlan != KeydrivePlan.FREE && isUpgrading
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonMethod: () => {
                        downgradePlan();
                        setIsDowngrading(false);
                    },
                    rightButtonText: "Continuar",
                    leftButtonMethod: () => {
                        setIsDowngrading(false);
                    },
                    leftButtonText: "Cancelar"
                }}
                dialogTitleCenter
                keyDriveDialogContent={{
                    dialogTitle: "Você está prestes a fazer um downgrade no seu plano!",
                    dialogIcon: <Check />,
                    dialogContent: (
                        <KeyDriveBodyMin 
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                            text={"Ao clicar em continuar, você concorda em trocar de plano assinado. Diferente de um upgrade, esta ação resultará em menos recursos do que os já contratados."}
                        />
                    ),
                    handleTogglDialog: () => {},
                    openDialog: profile?.signtureInformation?.currentPlan != KeydrivePlan.FREE && isDowngrading
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonMethod: () => {
                        setPricingModal(false);
                        setIsContractingSamePlan(false);
                    },
                    rightButtonText: "Continuar",
                    leftButtonMethod: () => {
                    },
                    leftButtonText: undefined
                }}
                dialogTitleCenter
                keyDriveDialogContent={{
                    dialogTitle: "Você está tentando assinar um plano já assinado!",
                    dialogIcon: <Check />,
                    dialogContent: (
                        <KeyDriveBodyMin 
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                            text={" Não é possível assinar um plano que você já tenha assinado. Caso precise de uma solução personalizada, entre em contato com nosso time comercial para que possamos entender melhor sua demanda."}
                        />
                    ),
                    handleTogglDialog: () => {},
                    openDialog: profile?.signtureInformation?.currentPlan != KeydrivePlan.FREE && isContractingSamePlan
                }}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonMethod: () => {
                        setIsUserAlreadyDowngradedOrUpgraded(false);
                    },
                    rightButtonText: "Continuar",
                    leftButtonMethod: () => {
                    },
                    leftButtonText: undefined
                }}
                dialogTitleCenter
                keyDriveDialogContent={{
                    dialogTitle: "Você já fez um upgrade ou downgrade recentemente",
                    dialogIcon: <CloseSharp />,
                    dialogContent: (
                        <KeyDriveBodyMin 
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                            text={`Verificamos que você já realizou um downgrade ou upgrade recentemente. 
                                Para garantir a qualidade dos nossos serviços, não permitimos alterações de plano a todo momento. 
                                Conforme consulta em nosso sistema, sua equipe poderá realizar um novo upgrade ou downgrade 
                                a partir de: ${moment(profile?.signtureInformation?.contractDowngradeUpgrade?.whenCanUpgradeOrDowngradeAgain!).format("DD/MM/YYYY")}. 
                                Caso tenha dúvidas, entre em contato com nosso suporte.`}
                        />
                    ),
                    handleTogglDialog: () => {},
                    openDialog: isUserAlreadyDowngradedOrUpgraded
                }}
            />
            
        </Box>
    );
}